<script setup lang="ts">
import type {
  AttributeSelections,
  AttributeSelectionValue,
  NormalizedProductAttribute,
} from '../productAttributes';
import { currencyFromLegacy, formatMoney, type CurrencyContext } from '../money';

const props = withDefaults(defineProps<{
  attributes: NormalizedProductAttribute[];
  modelValue: AttributeSelections;
  currencySymbol?: string;
  currency?: CurrencyContext;
  compact?: boolean;
  showErrors?: boolean;
}>(), {
  currencySymbol: '$',
  compact: false,
  showErrors: false,
});

const emit = defineEmits<{
  'update:modelValue': [AttributeSelections];
  change: [];
}>();

function selected(attr: NormalizedProductAttribute): AttributeSelectionValue | undefined {
  return props.modelValue[attr.slug];
}

function presentation(attr: NormalizedProductAttribute) {
  if (attr.presentation !== 'auto') return attr.presentation;
  if (attr.options.some((option) => option.image)) return 'image_cards';
  return attr.selection === 'single' && attr.options.length > 5 ? 'dropdown' : 'chips';
}

function commit(attr: NormalizedProductAttribute, value: AttributeSelectionValue) {
  emit('update:modelValue', { ...props.modelValue, [attr.slug]: value });
  emit('change');
}

function toggle(attr: NormalizedProductAttribute, value: string) {
  const current = Array.isArray(selected(attr)) ? selected(attr) as string[] : [];
  commit(attr, current.includes(value) ? current.filter((item) => item !== value) : [...current, value]);
}

function setQuantity(attr: NormalizedProductAttribute, value: string, event: Event) {
  const current = selected(attr);
  const quantities = current && typeof current === 'object' && !Array.isArray(current) ? current : {};
  commit(attr, { ...quantities, [value]: Math.max(0, Math.round(Number((event.target as HTMLInputElement).value) || 0)) });
}

function empty(attr: NormalizedProductAttribute): boolean {
  const value = selected(attr);
  if (typeof value === 'string') return value === '';
  if (Array.isArray(value)) return value.length === 0;
  if (value && typeof value === 'object') return !Object.values(value).some((qty) => Number(qty) > 0);
  return true;
}

function price(value?: number): string {
  return Number(value) ? `+${formatMoney(Number(value), currencyFromLegacy(props.currency, props.currencySymbol))}` : '';
}
</script>

<template>
  <div class="vptw:flex vptw:flex-col" :class="compact ? 'vptw:gap-3' : 'vptw:gap-4'">
    <fieldset v-for="attr in attributes" :key="attr.slug" class="vptw:m-0! vptw:min-w-0 vptw:border-0! vptw:p-0!">
      <legend class="vptw:mb-2 vptw:block vptw:text-sm vptw:font-semibold vptw:text-(--vs-ink-2)">
        {{ attr.name }} <span v-if="attr.required" class="vptw:text-red-500">*</span>
      </legend>

      <select
        v-if="attr.selection === 'single' && presentation(attr) === 'dropdown'"
        :aria-label="attr.name"
        class="vptw:w-full vptw:rounded-xl vptw:border vptw:border-(--vs-line) vptw:bg-white vptw:px-3 vptw:py-2 vptw:text-sm vptw:outline-none vptw:focus:border-(--vs-accent) vptw:focus:ring-2 vptw:focus:ring-(--vs-accent-soft)"
        :value="typeof selected(attr) === 'string' ? selected(attr) : ''"
        @change="commit(attr, ($event.target as HTMLSelectElement).value)"
      >
        <option value="">Choose {{ attr.name }}</option>
        <option v-for="option in attr.options" :key="option.value" :value="option.value">
          {{ option.label }} {{ price(option.priceAdjust) }}
        </option>
      </select>

      <div v-else-if="attr.selection === 'quantity'" class="vptw:grid vptw:grid-cols-2 vptw:gap-2">
        <label v-for="option in attr.options" :key="option.value" class="vptw:flex vptw:items-center vptw:justify-between vptw:gap-2 vptw:rounded-xl vptw:border vptw:border-(--vs-line) vptw:bg-white vptw:px-3 vptw:py-2">
          <span class="vptw:min-w-0 vptw:text-xs vptw:text-(--vs-ink-2)">{{ option.label }} <small class="vptw:text-(--vs-ink-3)">{{ price(option.priceAdjust) }}</small></span>
          <input type="number" min="0" step="1" class="vptw:w-16 vptw:rounded-lg vptw:border vptw:border-(--vs-line) vptw:px-2 vptw:py-1 vptw:text-center vptw:text-sm" :value="(selected(attr) as Record<string, number> | undefined)?.[option.value] ?? 0" @input="setQuantity(attr, option.value, $event)" />
        </label>
      </div>

      <div v-else class="vptw:flex vptw:flex-wrap vptw:gap-2">
        <label v-for="option in attr.options" :key="option.value" class="vptw:cursor-pointer">
          <input
            :type="attr.selection === 'multiple' ? 'checkbox' : 'radio'"
            :name="attr.slug"
            :value="option.value"
            class="vptw:sr-only vptw:peer"
            :checked="attr.selection === 'multiple' ? (Array.isArray(selected(attr)) && (selected(attr) as string[]).includes(option.value)) : selected(attr) === option.value"
            @change="attr.selection === 'multiple' ? toggle(attr, option.value) : commit(attr, option.value)"
          />
          <span
            class="vptw:flex vptw:min-h-10 vptw:items-center vptw:gap-2 vptw:rounded-xl vptw:border vptw:border-(--vs-line) vptw:bg-white vptw:px-3 vptw:py-2 vptw:text-sm vptw:text-(--vs-ink-2) vptw:transition vptw:hover:border-(--vs-line) vptw:peer-focus-visible:ring-2 vptw:peer-focus-visible:ring-(--vs-accent) vptw:peer-checked:border-(--vs-accent) vptw:peer-checked:bg-(--vs-accent-soft) vptw:peer-checked:text-(--vs-accent)"
            :class="presentation(attr) === 'image_cards' ? 'vptw:min-w-24 vptw:flex-col vptw:text-center' : ''"
          >
            <img v-if="option.image" :src="option.image" :alt="option.label" class="vptw:size-14 vptw:rounded-lg vptw:object-contain" loading="lazy" />
            <span>{{ option.label }} <small v-if="price(option.priceAdjust)" class="vptw:text-[10px] vptw:text-(--vs-ink-2)">{{ price(option.priceAdjust) }}</small></span>
          </span>
        </label>
      </div>

      <p v-if="showErrors && attr.required && empty(attr)" class="vptw:mt-1.5 vptw:mb-0 vptw:text-xs vptw:text-red-600" role="alert">
        Choose {{ attr.name }}.
      </p>
    </fieldset>
  </div>
</template>
